import { useParams, useNavigate } from "react-router";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { useEncryption } from "@/hooks/useEncryption";
import { Card, CardContent } from "@/components/ui/card";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { EmptyState } from "@/components/shared/EmptyState";
import { ChevronRight, Lock, CalendarDays } from "lucide-react";
import { useState, useEffect } from "react";

interface Session {
  _key: string;
  name: string;
  icon: string;
  description: string;
  proposed_dates: string[];
  fixed_date: string | null;
  points: number | null;
}

function formatTimeToEvent(dateStr: string): string {
  const now = new Date();
  const event = new Date(dateStr + "T00:00:00");
  const diffDays = Math.round((event.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) {
    const abs = Math.abs(diffDays);
    if (abs === 1) return "1 day ago";
    if (abs < 7) return `${abs} days ago`;
    if (abs < 14) return "1 week ago";
    return `${Math.floor(abs / 7)} weeks ago`;
  }
  if (diffDays === 0) return "today";
  if (diffDays === 1) return "tomorrow";
  if (diffDays < 7) return `in ${diffDays} days`;
  if (diffDays < 14) return "in 1 week";
  if (diffDays < 30) return `in ${Math.floor(diffDays / 7)} weeks`;
  if (diffDays < 60) return "in 1 month";
  return `in ${Math.floor(diffDays / 30)} months`;
}

function formatDisplayDate(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

export function ClientSessions() {
  const { jwt } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { decryptList, hasKey } = useEncryption(user?._key);
  const { data: rawSessions, loading, error } = useApi<Session[]>(
    `/api/client/${jwt}/sessions`,
  );
  const [sessions, setSessions] = useState<Session[] | null>(null);

  // Decrypt session names/descriptions
  useEffect(() => {
    if (!rawSessions) return;
    decryptList(rawSessions as unknown as Record<string, unknown>[], ["name", "description"])
      .then((d) => setSessions(d as unknown as Session[]));
  }, [rawSessions, decryptList]);

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-app-danger text-body p-4">{error}</p>;

  if (!sessions || sessions.length === 0) {
    return (
      <EmptyState
        icon="📅"
        title="No sessions yet"
        description="Sessions will appear here once your admin creates them."
      />
    );
  }

  const draft = sessions.filter((s) => !s.fixed_date);
  const fixed = sessions.filter((s) => s.fixed_date);


  return (
    <div className="space-y-3 md:space-y-4">
      <h1 className="text-heading font-bold">Sessions</h1>

      {fixed.length > 0 && (
        <div className="space-y-2.5">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-app-success/15 flex items-center justify-center">
              <Lock className="w-3.5 h-3.5 text-app-success" />
            </div>
            <h2 className="text-body font-semibold text-app-text-muted">Scheduled</h2>
          </div>
          <div className="space-y-2">
            {fixed.map((s) => (
              <Card
                key={s._key}
                className="border-app-border border-l-4 border-l-app-success cursor-pointer hover:shadow-md hover:bg-app-surface-raised/50 hover:border-app-success/30 transition-all duration-200 overflow-hidden py-0 gap-0"
                onClick={() => navigate(`/client/${jwt}/sessions/${s._key}`)}
              >
                <CardContent className="p-2 md:p-2.5 flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-app-success/10 flex items-center justify-center text-lg shrink-0">
                    {s.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-body font-semibold">{s.name}</h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-info font-medium text-app-success">
                        {formatDisplayDate(s.fixed_date!)}
                      </span>
                      <span className="text-xs bg-app-primary/10 text-app-primary px-2 py-0.5 rounded-full font-semibold">
                        {s.points} pts
                      </span>
                      <span className="text-xs bg-app-surface-raised text-app-text-muted px-2 py-0.5 rounded-full border border-app-border/40">
                        {formatTimeToEvent(s.fixed_date!)}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-app-text-muted shrink-0" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {draft.length > 0 && (
        <div className="space-y-2.5">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-app-secondary/15 flex items-center justify-center">
              <CalendarDays className="w-3.5 h-3.5 text-app-secondary" />
            </div>
            <h2 className="text-body font-semibold text-app-text-muted">Proposed</h2>
          </div>
          <div className="space-y-2">
            {draft.map((s) => (
              <Card
                key={s._key}
                className="border-app-border border-l-4 border-l-app-secondary cursor-pointer hover:shadow-md hover:bg-app-surface-raised/50 hover:border-app-secondary/30 transition-all duration-200 overflow-hidden py-0 gap-0"
                onClick={() => navigate(`/client/${jwt}/sessions/${s._key}`)}
              >
                <CardContent className="p-2 md:p-2.5 flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-app-secondary/10 flex items-center justify-center text-lg shrink-0">
                    {s.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-body font-semibold">{s.name}</h3>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-xs bg-app-secondary/10 text-app-secondary px-2 py-0.5 rounded-full font-medium">
                        📋 {(s.proposed_dates || []).length} date{(s.proposed_dates || []).length !== 1 ? "s" : ""} proposed
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-app-text-muted shrink-0" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
